import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface PublicToken {
  t: string | null;
  l?: number;
  w: boolean;
}
interface View {
  /** Absent on light (post-guess) responses — the client keeps its own copy. */
  tokens?: PublicToken[];
  guesses: { g: string; hits: number; t?: boolean }[];
  words: number;
  tips: number;
  /** Vital-list section, the always-on hint. */
  topic?: string;
  title?: string;
}
interface GuessExtra {
  hits: number;
  reveal: [number, string][];
  word?: string;
}

const COPY = {
  bad_word: "Тільки слова (до 4)",
  already_guessed: "Вже було",
  too_many: "Ліміт спроб",
  no_tips: "Підказки закінчились",
  unsupported: "Не можна",
};

export function Redactle({ date }: { date?: string | undefined }) {
  const g = useGame<View>("redactle", COPY, date);
  const [word, setWord] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [tokens, setTokens] = useState<PublicToken[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  // Keep a local token list: full lists arrive on /state (and on finish), light
  // guess responses only carry the revealed [index, word] pairs.
  const serverTokens = state?.view.tokens;
  useEffect(() => {
    if (serverTokens) setTokens(serverTokens);
  }, [serverTokens]);
  const revealedCount = useMemo(() => tokens.filter((t) => t.w && t.t !== null).length, [tokens]);

  const applyReveal = (reveal: [number, string][] | undefined) => {
    if (!reveal || reveal.length === 0) return;
    setTokens((prev) => {
      const next = [...prev];
      for (const [i, word] of reveal) {
        const t = next[i];
        if (t) next[i] = { t: word, w: true };
      }
      return next;
    });
  };

  if (g.screen.kind === "loading") return <Shell><Centered>завантажую статтю…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const lastGuess = v.guesses[v.guesses.length - 1];
  const hl = highlight ?? lastGuess?.g ?? null;

  const submit = async () => {
    const w = word.trim();
    if (!w || finished || g.busy) return;
    inputRef.current?.focus();
    const next = await g.run(() => api.guess<View>("redactle", w, date));
    if (next) {
      setWord("");
      setHighlight(null);
      const extra = next.extra as GuessExtra | undefined;
      applyReveal(extra?.reveal);
      if (next.status === "playing") g.showToast(`${w}: ${extra?.hits ?? 0}`, 900);
    }
  };

  const hint = async () => {
    if (finished || g.busy) return;
    const next = await g.run(() => api.guess<View>("redactle", "?", date));
    if (next) {
      const extra = next.extra as GuessExtra | undefined;
      applyReveal(extra?.reveal);
      if (extra?.word) setHighlight(extra.word);
    }
  };

  // Paragraph split: tokens contain "\n\n" in non-word tokens.
  const paragraphs: PublicToken[][] = [];
  let cur: PublicToken[] = [];
  for (const t of tokens) {
    if (!t.w && t.t && t.t.includes("\n")) {
      if (cur.length) paragraphs.push(cur);
      cur = [];
    } else cur.push(t);
  }
  if (cur.length) paragraphs.push(cur);

  return (
    <Shell header={<Header left={`#${s.number}`} title="REDACTLE" right={`🔥 ${s.me.streak}`} />}>
      <div className="flex items-center justify-between px-4 pb-1 text-xs text-tghint">
        <span>
          спроб: <b className="text-tgtext">{v.guesses.length}</b>
          {v.tips > 0 ? <> · 💡{v.tips}</> : null}
          {v.topic ? <> · тема: <b className="text-tgtext">{v.topic}</b></> : null}
        </span>
        <span>відкрито: <b className="text-tgtext">{revealedCount}</b> / {v.words}</span>
      </div>

      <form
        className="flex gap-2 px-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          ref={inputRef}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          readOnly={finished}
          placeholder={finished ? "гра закінчена" : "слово або кілька слів"}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="none"
          spellCheck
          enterKeyHint="send"
          autoFocus
          className="h-[44px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.7)] focus:border-tgbutton"
        />
        <button
          type="submit"
          disabled={finished || g.busy || !word.trim()}
          onMouseDown={(e) => e.preventDefault()}
          className="h-[44px] rounded-xl bg-tgbutton px-4 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
        >
          {g.busy ? "…" : "→"}
        </button>
      </form>

      {v.guesses.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: "none" }}>
          {[...v.guesses].reverse().slice(0, 30).map((x) => (
            <button
              key={x.g}
              type="button"
              onClick={() => setHighlight(x.g)}
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs ${hl === x.g ? "bg-tgbutton text-tgbuttontext" : "bg-[rgba(128,128,128,0.18)]"}`}
            >
              {x.t ? "💡 " : ""}{x.g} <span className="opacity-70">{x.hits}</span>
            </button>
          ))}
        </div>
      )}

      <div
        ref={textRef}
        className="flex-1 overflow-y-auto px-4 pb-4 text-[15px] leading-[1.65]"
        onTouchMove={() => inputRef.current?.blur()}
      >
        {paragraphs.map((p, i) => (
          <p key={i} className={i === 0 ? "mb-3 text-lg font-bold" : "mb-3"}>
            {p.map((t, j) =>
              t.t === null ? (
                <span
                  key={j}
                  className="mx-[1px] inline-block select-none rounded-[3px] align-baseline"
                  style={{
                    width: `${Math.max(1, t.l ?? 3) * 0.58}em`,
                    height: "0.95em",
                    verticalAlign: "-0.15em",
                    background: "rgba(128,128,128,0.45)",
                  }}
                />
              ) : (
                <span
                  key={j}
                  className={t.w && hl && t.t.toLowerCase().startsWith(hl.toLowerCase().slice(0, Math.max(3, hl.length - 2))) && t.t.length <= hl.length + 3 ? "rounded-sm bg-warm/40" : ""}
                >
                  {t.t}
                </span>
              ),
            )}
          </p>
        ))}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <button
            type="button"
            disabled={g.busy}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void hint()}
            className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
          >
            💡 підказка
          </button>
          {confirm ? (
            <button
              type="button"
              onClick={() => {
                setConfirm(false);
                void g.run(() => api.giveUp<View>("redactle", date));
              }}
              className="flex-1 rounded-xl bg-cold/80 py-3 text-sm font-semibold text-white active:opacity-70"
            >
              точно здатись?
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirm(true);
                window.setTimeout(() => setConfirm(false), 3000);
              }}
              className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70"
            >
              🏳 здатись
            </button>
          )}
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={s.status === "won" ? "Є стаття" : "Здався"}
          subtitle={
            s.status === "won"
              ? `${v.guesses.length} спроб · стрік ${s.me.streak} 🔥`
              : `це було «${v.title ?? "?"}» · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.title && <div className="mt-4 text-center text-lg font-semibold">{v.title}</div>}
        </ResultSheet>
      )}
    </Shell>
  );
}
